"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GenericObject = void 0;
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const uuid_1 = require("uuid");
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const set_1 = __importDefault(require("lodash/set"));
const get_1 = __importDefault(require("lodash/get"));
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const camelCase_1 = __importDefault(require("lodash/camelCase"));
const upperFirst_1 = __importDefault(require("lodash/upperFirst"));
const merge_1 = __importDefault(require("lodash/merge"));
/**
 * Parent class for all object classes. The attributes field corresponds to the
 * fields in the database, while the data is a special field for object that
 * contains a json string, so any additional field should be in data.
 */
class GenericObject {
    constructor(attributes, isNew = true) {
        this._isValid = false;
        this._errors = [];
        this._deleted = false;
        this._attributes = this._prepareAttributes(attributes);
        this._isNew = isNew;
        this._wasFrozen = false;
    }
    get id() {
        // TODO: we should allow integer id, since some objects will not have a uuid, or at least will have a valid integer id for better performance
        return this._attributes.id;
    }
    get uuid() {
        return this._attributes.id;
    }
    get isValid() {
        return this._isValid;
    }
    get attributes() {
        return this._attributes;
    }
    // TODO: Should use getErrors instead
    get errors() {
        return this._errors;
    }
    // TODO: Should not be set directly
    set errors(errors) {
        this._errors = errors;
    }
    mergeAttributes(updatedAttributes) {
        (0, merge_1.default)(this._attributes, (0, cloneDeep_1.default)(updatedAttributes));
    }
    _prepareAttributes(attributes) {
        const { data, id, is_frozen } = attributes, attribs = __rest(attributes, ["data", "id", "is_frozen"]);
        const newAttribs = Object.assign(Object.assign({}, (0, cloneDeep_1.default)(attribs)), { id: id ? id : (0, uuid_1.v4)(), data: data ? (0, cloneDeep_1.default)(data) : {}, is_frozen: is_frozen ? true : false });
        return newAttribs;
    }
    /**
     * @deprecated Use the .attributes accessor instead.
     */
    getAttributes() {
        return this._attributes;
    }
    /**
     * Resets all attributes
     *
     * TODO, this results in a complete erasure of the attributes, this may not
     * be the intended result, so it should be removed. If necessary a new
     * setMultiple method could be added
     * @param {Partial<T>} attributes
     * @memberof GenericObject
     * @deprecated If the intention is to set all attributes, consider creating
     * a new objects instead. Otherwise, set each attribute independently.
     */
    setAttributes(attributes) {
        this._attributes = this._prepareAttributes(attributes);
        this._wasFrozen = this._attributes.is_frozen === true;
    }
    wasFrozen() {
        return this._wasFrozen;
    }
    isFrozen() {
        return this.get('is_frozen') === true;
    }
    _innerSet(path, value) {
        (0, set_1.default)(this._attributes, path, value);
    }
    set(path, value) {
        const oldValue = (0, get_1.default)(this._attributes, path);
        if (!(0, isEqual_1.default)(oldValue, value)) {
            this._innerSet(path, value);
        }
    }
    _innerSetData(path, value) {
        (0, set_1.default)(this._attributes.data, path, value);
    }
    setData(path, value) {
        const oldValue = (0, get_1.default)(this._attributes.data, path);
        if (!(0, isEqual_1.default)(oldValue, value)) {
            this._innerSetData(path, value);
        }
    }
    /**
     * Clone this object. If deleteSpecifics is true, it removes all object's
     * ids and individual attributes like creation dates, but also fields that
     * contain links to children objects (like objects of different types that
     * have a foreign key to the current object). Implementers need to make sure
     * any such fields are removed.
     *
     * @protected
     * @param {boolean} [deleteSpecifics=true] If true, no ID or children
     * object's IDs will be copied, otherwise, it is a complete copy of the
     * attributes, including all children fields.
     * @param {boolean} [isNew=true] Whether the cloned object should be new or
     * not
     * @return {*}  {Partial<T>}
     * @memberof GenericObject
     */
    clone(deleteSpecifics = true, isNew = true) {
        return new this.constructor(this.getClonedAttributes(deleteSpecifics), isNew);
    }
    duplicate(deleteId = true, isNew = true) {
        return this.clone(deleteId, isNew);
    }
    validate() {
        this._isValid = true;
        this._errors = [];
        return true;
    }
    getErrors() {
        return this._errors;
    }
    isDeleted() {
        return this._deleted;
    }
    setDeleted() {
        this._deleted = true;
    }
    isNew() {
        return this._isNew;
    }
    setNew(isNew = false) {
        this._isNew = isNew;
    }
    /**
     * Clone this object's attribute. If deleteSpecifics is true, it removes all
     * object's ids and individual attributes like creation dates, but also
     * fields that contain links to children objects (like objects of different
     * types that have a foreign key to the current object). Implementers need
     * to make sure any such fields are removed.
     *
     * @protected
     * @param {boolean} [deleteSpecifics=true] If true, no ID or children
     * object's IDs will be copied, otherwise, it is a complete copy of the
     * attributes, including all children fields.
     * @return {*}  {Partial<T>}
     * @memberof GenericObject
     */
    getClonedAttributes(deleteSpecifics = true) {
        const clonedAttributes = (0, cloneDeep_1.default)(this._attributes);
        if (deleteSpecifics) {
            delete clonedAttributes.id;
            if (clonedAttributes.integer_id) {
                delete clonedAttributes.integer_id;
            }
            if (clonedAttributes.created_at) {
                delete clonedAttributes.created_at;
            }
            if (clonedAttributes.updated_at) {
                delete clonedAttributes.updated_at;
            }
        }
        return clonedAttributes;
    }
    getId() {
        return this._attributes.id;
    }
    getShortenedId() {
        return this._attributes.id.substring(0, 7);
    }
    get(path, defaultValue = undefined) {
        const value = this._attributes[path] ? this._attributes[path] : (0, get_1.default)(this._attributes, path);
        if (defaultValue === undefined && value !== undefined) {
            return value;
        }
        return value === null || value === undefined || value === '' ? defaultValue : value;
    }
    getData(path, defaultValue = undefined) {
        const value = this._attributes.data[path] ? this._attributes.data[path] : (0, get_1.default)(this._attributes.data, path);
        if (defaultValue === undefined && value !== undefined) {
            return value;
        }
        return value === null || value === undefined || value === '' ? defaultValue : value;
    }
    getSingularName() {
        return this.constructor.displayName
            ? (0, camelCase_1.default)(this.constructor.displayName)
            : (0, camelCase_1.default)(this.constructor.name);
    }
    // override for special case like agency/agencies.
    // A pluralize library would be too slow and overkill for this simple purpose
    getPluralName() {
        return this.constructor.displayName
            ? (0, camelCase_1.default)(this.constructor.displayName) + 's'
            : (0, camelCase_1.default)(this.constructor.name) + 's';
    }
    getDisplayName() {
        return this.constructor.displayName ? this.constructor.displayName : this.constructor.name;
    }
    getCapitalizedSingularName() {
        return this.constructor.displayName
            ? (0, upperFirst_1.default)((0, camelCase_1.default)(this.constructor.displayName))
            : (0, upperFirst_1.default)((0, camelCase_1.default)(this.constructor.name));
    }
    // override for special case like agency/agencies.
    // A pluralize library would be too slow and overkill for this simple purpose
    getCapitalizedPluralName() {
        return this.constructor.displayName
            ? (0, upperFirst_1.default)((0, camelCase_1.default)(this.constructor.displayName)) + 's'
            : (0, upperFirst_1.default)((0, camelCase_1.default)(this.constructor.name)) + 's';
    }
}
exports.GenericObject = GenericObject;
//# sourceMappingURL=GenericObject.js.map